/**
 * Risk Analysis Page
 * Equity curve, advanced risk metrics, Monte Carlo results
 */

import React, { useEffect, useState } from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Gauge, Cell } from 'recharts';
import '../styles/validation.css';

const API_BASE = 'https://trading-api-1075463475276.us-central1.run.app/api/trading-validation';

const RiskAnalysis: React.FC = () => {
  const [equity, setEquity] = useState<any[]>([]);
  const [risks, setRisks] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [equityRes, riskRes] = await Promise.all([
          fetch(`${API_BASE}/equity-curve`),
          fetch(`${API_BASE}/risk-metrics`),
        ]);

        const equityData = await equityRes.json();
        const riskData = await riskRes.json();

        setEquity(equityData.data || []);
        setRisks(riskData.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching risk data:', error);
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 300000); // 5 min
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="validation-container"><div className="loading">Loading risk analysis...</div></div>;

  const getRiskColor = (value: number, metric: string) => {
    if (metric === 'sharpe') {
      if (value > 2) return '#4CAF50';
      if (value > 1) return '#FF9800';
      return '#F44336';
    }
    if (metric === 'sortino') {
      if (value > 3) return '#4CAF50';
      if (value > 2) return '#FF9800';
      return '#F44336';
    }
    if (metric === 'calmar') {
      if (value > 2) return '#4CAF50';
      if (value > 1) return '#FF9800';
      return '#F44336';
    }
    if (metric === 'max_drawdown') {
      if (value > -5) return '#4CAF50';
      if (value > -10) return '#FF9800';
      return '#F44336';
    }
    return '#2196F3';
  };

  return (
    <div className="validation-container">
      <div className="validation-header">
        <h1>Risk Analysis</h1>
        <p className="subtitle">Equity curve, risk metrics, stress testing</p>
      </div>

      {/* Risk Metrics Summary */}
      {risks && (
        <div className="risk-metrics-grid">
          <div className="risk-metric-card">
            <div className="metric-label">Sharpe Ratio</div>
            <div className="metric-value" style={{ color: getRiskColor(risks.sharpe_ratio, 'sharpe') }}>
              {risks.sharpe_ratio.toFixed(2)}
            </div>
            <div className="metric-description">Risk-adjusted returns (target: {'>'}2.0)</div>
          </div>

          <div className="risk-metric-card">
            <div className="metric-label">Sortino Ratio</div>
            <div className="metric-value" style={{ color: getRiskColor(risks.sortino_ratio, 'sortino') }}>
              {risks.sortino_ratio.toFixed(2)}
            </div>
            <div className="metric-description">Downside risk ratio (target: {'>'}3.0)</div>
          </div>

          <div className="risk-metric-card">
            <div className="metric-label">Calmar Ratio</div>
            <div className="metric-value" style={{ color: getRiskColor(risks.calmar_ratio, 'calmar') }}>
              {risks.calmar_ratio.toFixed(2)}
            </div>
            <div className="metric-description">Return/drawdown ratio (target: {'>'}2.0)</div>
          </div>

          <div className="risk-metric-card">
            <div className="metric-label">Max Drawdown</div>
            <div className="metric-value" style={{ color: getRiskColor(risks.max_drawdown, 'max_drawdown') }}>
              {risks.max_drawdown.toFixed(2)}%
            </div>
            <div className="metric-description">Peak-to-trough decline (target: {'<'}-5%)</div>
          </div>

          <div className="risk-metric-card">
            <div className="metric-label">Recovery Time</div>
            <div className="metric-value">{risks.recovery_days} days</div>
            <div className="metric-description">Days to recover from max drawdown</div>
          </div>

          <div className="risk-metric-card">
            <div className="metric-label">Volatility</div>
            <div className="metric-value">{(risks.volatility * 100).toFixed(1)}%</div>
            <div className="metric-description">Annualized standard deviation</div>
          </div>

          <div className="risk-metric-card">
            <div className="metric-label">Profit Factor</div>
            <div className="metric-value" style={{ color: risks.profit_factor > 1.5 ? '#4CAF50' : '#FF9800' }}>
              {risks.profit_factor.toFixed(2)}
            </div>
            <div className="metric-description">Gross profit / gross loss (target: {'>'}1.5)</div>
          </div>

          <div className="risk-metric-card">
            <div className="metric-label">Win/Loss Ratio</div>
            <div className="metric-value">{risks.win_loss_ratio.toFixed(2)}</div>
            <div className="metric-description">Avg win $ / avg loss $</div>
          </div>
        </div>
      )}

      {/* Equity Curve Chart */}
      <div className="widget chart-widget">
        <h3>Equity Curve (40 Days)</h3>
        {equity.length > 0 && (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={equity}>
              <defs>
                <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2196F3" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#2196F3" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip formatter={(value) => `$${Number(value).toFixed(0)}`} />
              <Area type="monotone" dataKey="equity" stroke="#2196F3" fillOpacity={1} fill="url(#colorEquity)" name="Equity" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Drawdown Analysis */}
      {equity.length > 0 && (
        <div className="widget chart-widget">
          <h3>Drawdown Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={equity}>
              <defs>
                <linearGradient id="colorDrawdown" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F44336" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#F44336" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip formatter={(value) => `${Number(value).toFixed(2)}%`} />
              <Area type="monotone" dataKey="drawdown_pct" stroke="#F44336" fillOpacity={1} fill="url(#colorDrawdown)" name="Drawdown %" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Stress Test Results */}
      {risks && risks.stress_tests && (
        <div className="widget">
          <h3>Monte Carlo Stress Testing (1,000 Simulations)</h3>
          <div className="stress-tests-grid">
            <div className="stress-test-item">
              <span className="label">Expected Return (Mean):</span>
              <span className="value">{risks.stress_tests.mean_return.toFixed(2)}%</span>
            </div>
            <div className="stress-test-item">
              <span className="label">Return Std Dev:</span>
              <span className="value">{risks.stress_tests.return_std_dev.toFixed(2)}%</span>
            </div>
            <div className="stress-test-item">
              <span className="label">5% Worst Case (VaR):</span>
              <span className="value highlight-danger">{risks.stress_tests.var_5pct.toFixed(2)}%</span>
            </div>
            <div className="stress-test-item">
              <span className="label">1% Worst Case (CVaR):</span>
              <span className="value highlight-danger">{risks.stress_tests.cvar_1pct.toFixed(2)}%</span>
            </div>
            <div className="stress-test-item">
              <span className="label">Win Rate in Simulations:</span>
              <span className="value">{(risks.stress_tests.win_rate_pct * 100).toFixed(1)}%</span>
            </div>
            <div className="stress-test-item">
              <span className="label">Robustness Score:</span>
              <span className="value" style={{ color: risks.stress_tests.robustness_pct > 80 ? '#4CAF50' : '#FF9800' }}>
                {risks.stress_tests.robustness_pct.toFixed(1)}%
              </span>
            </div>
          </div>
          <div className="stress-tests-interpretation">
            <p><strong>Interpretation:</strong> System was tested against {risks.stress_tests.simulation_count} random market conditions.
              {risks.stress_tests.robustness_pct > 80 ?
                ' System shows strong robustness.' :
                ' System may need refinement for varying conditions.'}
            </p>
          </div>
        </div>
      )}

      {/* Risk Thresholds */}
      <div className="widget">
        <h3>Risk Thresholds & Status</h3>
        <div className="threshold-grid">
          <div className={`threshold-item ${risks?.sharpe_ratio > 2 ? 'pass' : 'warn'}`}>
            <span className="label">Sharpe Ratio</span>
            <span className="threshold">&gt; 2.0</span>
            <span className="status">{risks?.sharpe_ratio > 2 ? '✓ Pass' : '✗ Below Target'}</span>
          </div>
          <div className={`threshold-item ${risks?.max_drawdown > -5 ? 'pass' : 'warn'}`}>
            <span className="label">Max Drawdown</span>
            <span className="threshold">&gt; -5%</span>
            <span className="status">{risks?.max_drawdown > -5 ? '✓ Pass' : '✗ Below Target'}</span>
          </div>
          <div className={`threshold-item ${risks?.profit_factor > 1.5 ? 'pass' : 'warn'}`}>
            <span className="label">Profit Factor</span>
            <span className="threshold">&gt; 1.5</span>
            <span className="status">{risks?.profit_factor > 1.5 ? '✓ Pass' : '✗ Below Target'}</span>
          </div>
          <div className={`threshold-item ${risks?.win_loss_ratio > 1.5 ? 'pass' : 'warn'}`}>
            <span className="label">Win/Loss Ratio</span>
            <span className="threshold">&gt; 1.5</span>
            <span className="status">{risks?.win_loss_ratio > 1.5 ? '✓ Pass' : '✗ Below Target'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskAnalysis;
